// Each Workspace Settings section has its own address, so it can be linked
// to, bookmarked and reached with Back. `settingsTab` selects the section in
// AccountView (billing's are in billing.ts).
export const workspaceSettingsTabPaths = {
    workspace: '/workspace/settings',
    'icp-analysis': '/workspace/icp',
    strategy: '/workspace/strategy',
    branding: '/workspace/branding',
    'settings-general': '/workspace/settings/general',
    'settings-newmember': '/workspace/settings/member-defaults',
    'settings-contentnotif': '/workspace/settings/content',
    'settings-roles': '/workspace/settings/roles',
    emails: '/workspace/notifications',
}

// Website Analysis's tabs (AccountICPAnalysis): Customer Profiles is the
// section's own address, the others sit under it (`icpTab`).
export const icpTabPaths = {
    profiles: workspaceSettingsTabPaths['icp-analysis'],
    industries: `${workspaceSettingsTabPaths['icp-analysis']}/industries`,
    personas: `${workspaceSettingsTabPaths['icp-analysis']}/personas`,
    products: `${workspaceSettingsTabPaths['icp-analysis']}/products`,
    'use-cases': `${workspaceSettingsTabPaths['icp-analysis']}/use-cases`,
    'pain-points': `${workspaceSettingsTabPaths['icp-analysis']}/pain-points`,
    questions: `${workspaceSettingsTabPaths['icp-analysis']}/questions`,
    'qualifying-questions': `${workspaceSettingsTabPaths['icp-analysis']}/qualifying-questions`,
    objections: `${workspaceSettingsTabPaths['icp-analysis']}/objections`,
    topics: `${workspaceSettingsTabPaths['icp-analysis']}/topics`,
}

// Strategy's tabs (AccountStrategy): Business Objectives is the section's own
// address, the others sit under it (`strategyTab`).
export const strategyTabPaths = {
    objectives: workspaceSettingsTabPaths.strategy,
    'audience-mapping': `${workspaceSettingsTabPaths.strategy}/audience-mapping`,
    // Topics & Perspectives (was Themes, at /themes, and Strategic Messaging, at /messaging - both redirect here)
    messaging: `${workspaceSettingsTabPaths.strategy}/perspectives`,
    // Spokespeople (was Voice Mapping, at /voice-mapping - which redirects here)
    'voice-mapping': `${workspaceSettingsTabPaths.strategy}/spokespeople`,
}

const workspaceSettingsNames = {
    'icp-analysis': 'Workspace Website Analysis',
    strategy: 'Workspace Strategy',
    branding: 'Workspace Branding',
    'settings-general': 'Workspace Settings General',
    'settings-newmember': 'Workspace Settings Member Defaults',
    'settings-contentnotif': 'Workspace Settings Content Notifications',
    'settings-roles': 'Workspace Settings Roles',
    emails: 'Workspace Notifications',
}

export default [
    {
		// Where Slack's install screen sends you back (see views/account/slackConnect.ts)
		path: '/integrations/slack/callback',
		name: 'Slack Callback',
		component: () => import('@/views/account/SlackCallback.vue'),
		meta: {
			layout: 'Default',
			abilities: [['manage', 'workspace-settings']],
		}
	},
    {
		path: '/member/:id/:tab?/:subtab?/:idForAction?/:action?',
		name: 'Workspace Member',
		component: () => import('@/views/team/member/WorkspaceMember.vue'),
		props: true,
		meta: {
			layout: 'Default',
			abilities: [['view', 'workspace-schedules'], ['manage', 'my-profile'], ['manage', 'workspace-member-settings']],
			ownerIdParameter: 'id',
			nav: 'workspace',
		}
	},
	{
		path: '/workspace',
		name: 'Workspace',
		component: () => import('@/views/team/Workspace.vue'),
		meta: {
			layout: 'Default',
			abilities: [['view', 'workspace-members']],
			nav: 'workspace',
		}
	},
	{
		path: '/workspace/refresh',
		name: 'Workspace Refresh',
		component: () => import('@/views/team/Workspace.vue'),
		meta: {
			layout: 'Default',
			abilities: [['manage', 'workspace-members']],
		}
	},
	{
		path: '/workspace/invited',
		name: 'Workspace Invite',
		component: () => import('@/views/team/WorkspaceInviteView.vue'),
		meta: {
			layout: 'Default',
			abilities: [['manage', 'invitations']],
			nav: 'workspace',
		}
	},
    {
		path: workspaceSettingsTabPaths.workspace,
		name: 'Workspace Settings',
		component: () => import('@/views/account/AccountView.vue'),
		meta: {
			layout: 'Default',
			abilities: [['manage', 'workspace-settings']],
			settingsTab: 'workspace',
		}
	},
	...Object.entries(workspaceSettingsNames).map(([tab, name]) => ({
		path: workspaceSettingsTabPaths[tab],
		name,
		component: () => import('@/views/account/AccountView.vue'),
		meta: {
			layout: 'Default',
			abilities: [['manage', 'workspace-settings']],
			settingsTab: tab,
		},
	})),
	...[
		['industries', 'Workspace Website Analysis Industries'],
		['personas', 'Workspace Website Analysis Buyer Personas'],
		['products', 'Workspace Website Analysis Products'],
		['use-cases', 'Workspace Website Analysis Use Cases'],
		['pain-points', 'Workspace Website Analysis Pain Points'],
		['questions', 'Workspace Website Analysis Buyer Questions'],
		['qualifying-questions', 'Workspace Website Analysis Qualifying Questions'],
		['objections', 'Workspace Website Analysis Objections'],
		['topics', 'Workspace Website Analysis Key Topics'],
	].map(([icpTab, name]) => ({
		path: icpTabPaths[icpTab],
		name,
		component: () => import('@/views/account/AccountView.vue'),
		meta: {
			layout: 'Default',
			abilities: [['manage', 'workspace-settings']],
			settingsTab: 'icp-analysis',
			icpTab,
		},
	})),
	...[
		['audience-mapping', 'Workspace Strategy Audience Mapping'],
		['messaging', 'Workspace Strategy Perspectives'],
		// Spokespeople is off for now (strategyTabs): its address goes to Perspectives
		// ['voice-mapping', 'Workspace Strategy Spokespeople'],
	].map(([strategyTab, name]) => ({
		path: strategyTabPaths[strategyTab],
		name,
		component: () => import('@/views/account/AccountView.vue'),
		meta: {
			layout: 'Default',
			abilities: [['manage', 'workspace-settings']],
			settingsTab: 'strategy',
			strategyTab,
		},
	})),
	// Content Types (once Content Mapping) left the Strategy: old links land on its first section
	...['content-types', 'content-mapping'].map((old) => ({
		path: `${workspaceSettingsTabPaths.strategy}/${old}`,
		redirect: strategyTabPaths.objectives,
	})),
	// Spokespeople (once Voice Mapping, and The Plan) is off for now: its addresses land on Perspectives
	...['plan', 'voice-mapping', 'spokespeople'].map((old) => ({
		path: `${workspaceSettingsTabPaths.strategy}/${old}`,
		redirect: strategyTabPaths.messaging,
	})),
	// Topics & Perspectives was Themes, and Strategic Messaging before that: old links still land on it
	...['themes', 'messaging'].map((old) => ({
		path: `${workspaceSettingsTabPaths.strategy}/${old}`,
		redirect: strategyTabPaths.messaging,
	})),
	{
		path: '/workspace/settings/pixel',
		name: 'Workspace Settings Add Pixel',
		component: () => import('@/views/account/AccountAddPixel.vue'),
		meta: {
			layout: 'Default',
			abilities: [['manage', 'workspace-settings']],
		}
	},
	{
		path: '/workspace/settings/add',
		name: 'Workspace Settings Add Workspace',
		component: () => import('@/views/account/AccountAddWorkspace.vue'),
		meta: {
			layout: 'Default',
			abilities: [['manage', 'workspace-settings']],
		}
	},
]